"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type BayesResult,
  bayesFromEvidence,
  bayesFromRates,
  evidenceBounds,
  naturalFrequencies,
} from "@/lib/tool-kit/stats-descriptive/bayes";
import {
  formatPercent,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import { parseProbability } from "@/lib/tool-kit/stats-descriptive/inputs";

/** English strings. A translated content file overrides these through `ui`. */
export const BAYES_THEOREM_CALCULATOR_STRINGS = {
  modeLabel: "What do you know?",
  modeRates: "P(A), P(B|A) and P(B|not A)",
  modeEvidence: "P(A), P(B|A) and P(B)",
  unitLabel: "Enter probabilities as",
  unitPercent: "Percentages (1 means 1%)",
  unitDecimal: "Decimals (0.01 means 1%)",
  priorLabel: "P(A) — the prior: how common A is (e.g. prevalence)",
  likelihoodLabel: "P(B|A) — chance of B when A is true (e.g. sensitivity)",
  falsePositiveLabel:
    "P(B|not A) — chance of B when A is false (e.g. false-positive rate)",
  evidenceLabel: "P(B) — overall chance of B",
  inputHint: "Fractions such as 1/250, and values with a % sign, also work.",
  populationLabel: "Show natural frequencies out of",
  tryExample: "Try an example",
  examplePrior: "1",
  exampleLikelihood: "90",
  exampleFalsePositive: "9",
  exampleEvidence: "9.81",
  clear: "Clear",
  enterPrompt:
    "Enter all three probabilities, each between 0 and 100% (or 0 and 1).",
  evidenceTooSmall:
    "P(B) cannot be below P(B|A) × P(A) = {low}: B happens at least that often through A alone.",
  evidenceTooLarge:
    "P(B) cannot be above {high} with these values of P(A) and P(B|A).",
  zeroEvidence:
    "With these values B never happens (P(B) = 0), so P(A|B) is undefined.",
  priorCertain:
    'With P(A) = 100% there is no "not A" group, so P(B) cannot tell you anything new: P(A|B) is 100%.',
  results: "Results",
  posterior: "P(A|B) — chance of A given B",
  evidence: "P(B) — overall chance of B",
  posteriorNotB: "P(A|not B)",
  negativePredictive: "P(not A|not B)",
  impliedFalsePositive: "Implied P(B|not A)",
  workingTitle: "How it was calculated",
  evidenceLine:
    "P(B) = P(B|A) × P(A) + P(B|not A) × (1 − P(A)) = {lik} × {prior} + {fpr} × {notPrior} = {pB}",
  bayesLine: "P(A|B) = P(B|A) × P(A) ÷ P(B) = {lik} × {prior} ÷ {pB} = {post}",
  impliedLine:
    "P(B|not A) = (P(B) − P(B|A) × P(A)) ÷ (1 − P(A)) = ({pB} − {lik} × {prior}) ÷ {notPrior} = {fpr}",
  tableTitle: "The same numbers as people",
  tableIntro:
    "Picture {total} people. These are the counts you would expect in each group:",
  colGroup: "Group",
  colB: "B (e.g. tests positive)",
  colNotB: "Not B (e.g. tests negative)",
  colTotal: "Total",
  rowA: "A (e.g. has the condition)",
  rowNotA: "Not A",
  rowTotal: "Total",
  frequencySentence:
    "Of the {b} people with B, {ab} also have A: {ab} ÷ {b} = {post}.",
  baseRateNote:
    "When A is rare, even an accurate test produces more false positives than true positives — the base-rate effect. That is how P(A|B) can sit far below P(B|A).",
} as const;

type Strings = typeof BAYES_THEOREM_CALCULATOR_STRINGS;
type Mode = "rates" | "evidence";
type Unit = "percent" | "decimal";

const POPULATIONS = ["1000", "10000", "100000"] as const;
type Population = (typeof POPULATIONS)[number];

interface Inputs {
  prior: string;
  likelihood: string;
  falsePositive: string;
  evidence: string;
}

const EMPTY: Inputs = {
  prior: "",
  likelihood: "",
  falsePositive: "",
  evidence: "",
};

type Outcome = { result: BayesResult } | { problem: string } | null;

function compute(
  inputs: Inputs,
  mode: Mode,
  unit: Unit,
  strings: Strings,
  fmt: NumberFormatter
): Outcome {
  const asPercent = unit === "percent";
  const prior = parseProbability(inputs.prior, asPercent);
  const likelihood = parseProbability(inputs.likelihood, asPercent);
  const third = parseProbability(
    mode === "rates" ? inputs.falsePositive : inputs.evidence,
    asPercent
  );
  if (prior === null || likelihood === null || third === null) {
    return null;
  }
  if (mode === "rates") {
    const result = bayesFromRates(prior, likelihood, third);
    return result ? { result } : { problem: strings.zeroEvidence };
  }
  if (prior >= 1) {
    return { problem: strings.priorCertain };
  }
  const result = bayesFromEvidence(prior, likelihood, third);
  const bounds = evidenceBounds(prior, likelihood);
  const show = (p: number) =>
    asPercent ? formatPercent(fmt, p) : formatStat(fmt, p);
  if (result === "evidence-too-small") {
    return { problem: t(strings.evidenceTooSmall, { low: show(bounds.low) }) };
  }
  if (result === "evidence-too-large") {
    return {
      problem: t(strings.evidenceTooLarge, { high: show(bounds.high) }),
    };
  }
  return result ? { result } : { problem: strings.zeroEvidence };
}

function FrequencyTable({
  result,
  total,
  fmt,
  show,
  strings,
}: {
  result: BayesResult;
  total: number;
  fmt: NumberFormatter;
  show: (p: number) => string;
  strings: Strings;
}) {
  const counts = naturalFrequencies(result, total);
  const people = (value: number) => fmt(value, { maximumFractionDigits: 1 });
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.tableTitle}</h3>
      <Explanation>{t(strings.tableIntro, { total: fmt(total) })}</Explanation>
      <ResultTable
        footer={[
          strings.rowTotal,
          people(counts.b),
          people(counts.notB),
          people(total),
        ]}
        head={[
          strings.colGroup,
          strings.colB,
          strings.colNotB,
          strings.colTotal,
        ]}
        rows={[
          [
            strings.rowA,
            people(counts.aAndB),
            people(counts.aAndNotB),
            people(counts.a),
          ],
          [
            strings.rowNotA,
            people(counts.notAAndB),
            people(counts.notAAndNotB),
            people(counts.notA),
          ],
        ]}
        wrapFirstColumn
      />
      {counts.b > 0 ? (
        <Explanation>
          {t(strings.frequencySentence, {
            b: people(counts.b),
            ab: people(counts.aAndB),
            post: show(result.posterior),
          })}
        </Explanation>
      ) : null}
    </div>
  );
}

function Working({
  result,
  mode,
  fmt,
  strings,
}: {
  result: BayesResult;
  mode: Mode;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const dec = (p: number) => formatStat(fmt, p);
  const values = {
    lik: dec(result.likelihood),
    prior: dec(result.prior),
    fpr: dec(result.falsePositiveRate),
    notPrior: dec(1 - result.prior),
    pB: dec(result.evidence),
    post: dec(result.posterior),
  };
  return (
    <WorkingPanel title={strings.workingTitle}>
      {mode === "evidence" ? (
        <Formula>{t(strings.impliedLine, values)}</Formula>
      ) : (
        <Formula>{t(strings.evidenceLine, values)}</Formula>
      )}
      <Formula>{t(strings.bayesLine, values)}</Formula>
      <Explanation>{strings.baseRateNote}</Explanation>
    </WorkingPanel>
  );
}

export default function BayesTheoremCalculatorWidget() {
  const strings = useToolUi(BAYES_THEOREM_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("rates");
  const [unit, setUnit] = useState<Unit>("percent");
  const [inputs, setInputs] = useState<Inputs>(EMPTY);
  const [population, setPopulation] = useState<Population>("10000");

  const outcome = compute(inputs, mode, unit, strings, fmt);
  const show = (p: number) =>
    unit === "percent" ? formatPercent(fmt, p) : formatStat(fmt, p);
  const update = (key: keyof Inputs) => (value: string) =>
    setInputs((previous) => ({ ...previous, [key]: value }));

  function fillExample() {
    setUnit("percent");
    setInputs({
      prior: strings.examplePrior,
      likelihood: strings.exampleLikelihood,
      falsePositive: strings.exampleFalsePositive,
      evidence: strings.exampleEvidence,
    });
  }

  const result = outcome && "result" in outcome ? outcome.result : null;
  const optional = (value: number | null) =>
    value === null ? "—" : show(value);

  return (
    <>
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <PillGroup
            label={strings.modeLabel}
            name="bt-mode"
            onChange={setMode}
            options={[
              { value: "rates", label: strings.modeRates },
              { value: "evidence", label: strings.modeEvidence },
            ]}
            value={mode}
          />
          <PillGroup
            label={strings.unitLabel}
            name="bt-unit"
            onChange={setUnit}
            options={[
              { value: "percent", label: strings.unitPercent },
              { value: "decimal", label: strings.unitDecimal },
            ]}
            value={unit}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="bt-prior"
            label={strings.priorLabel}
            numeric
            onChange={update("prior")}
            value={inputs.prior}
          />
          <TextField
            id="bt-likelihood"
            label={strings.likelihoodLabel}
            numeric
            onChange={update("likelihood")}
            value={inputs.likelihood}
          />
          {mode === "rates" ? (
            <TextField
              id="bt-false-positive"
              label={strings.falsePositiveLabel}
              numeric
              onChange={update("falsePositive")}
              value={inputs.falsePositive}
            />
          ) : (
            <TextField
              id="bt-evidence"
              label={strings.evidenceLabel}
              numeric
              onChange={update("evidence")}
              value={inputs.evidence}
            />
          )}
        </div>
        <p className="text-muted-foreground text-xs">{strings.inputHint}</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-48">
            <SelectField
              id="bt-population"
              label={strings.populationLabel}
              onChange={setPopulation}
              options={POPULATIONS.map((value) => ({
                value,
                label: fmt(Number(value)),
              }))}
              value={population}
            />
          </div>
          <button
            className={BUTTON_SECONDARY}
            onClick={fillExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInputs(EMPTY)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {outcome && "problem" in outcome ? (
        <ErrorNotice>{outcome.problem}</ErrorNotice>
      ) : null}
      {result ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.posterior}
                value={show(result.posterior)}
              />
              <StatCard
                label={strings.evidence}
                value={show(result.evidence)}
              />
              <StatCard
                label={strings.posteriorNotB}
                value={optional(result.posteriorGivenNotB)}
              />
              <StatCard
                label={strings.negativePredictive}
                value={optional(result.notAGivenNotB)}
              />
              {mode === "evidence" ? (
                <StatCard
                  label={strings.impliedFalsePositive}
                  value={show(result.falsePositiveRate)}
                />
              ) : null}
            </StatGrid>
          </div>
          <FrequencyTable
            fmt={fmt}
            result={result}
            show={show}
            strings={strings}
            total={Number(population)}
          />
          <Working fmt={fmt} mode={mode} result={result} strings={strings} />
        </section>
      ) : null}
      {outcome ? null : <Notice>{strings.enterPrompt}</Notice>}
    </>
  );
}
